import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import PageTourButton from '../guide/PageTourButton';
import { getDashboardSteps } from '../guide/toursteps';
import {
    Layout,
    Row,
    Col,
    Card,
    Table,
    Tag,
    Typography,
    DatePicker,
    Select,
    Button,
    Space,
    Spin,
    Modal,
    Segmented,
} from 'antd';
import {
    SearchOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    CalendarOutlined,
    ExpandAltOutlined,
    LeftOutlined,
    RightOutlined,
    FileExcelOutlined,
    FilePdfOutlined,
    MessageOutlined,
    SendOutlined,
    TeamOutlined,
    ContactsOutlined,
} from '@ant-design/icons';
import { Column, Line, Pie } from '@ant-design/charts';
import dayjs from 'dayjs';
import moment from 'moment-timezone';
import useDashboardStore from '../../stores/useDashboardStore';
import session from '../../utils/session';
import config from '../../config';
import UserAvatar from '../common/UserAvatar';
import { useFeature, FeatureLockCard } from '../common/FeatureGate';
import { exportDashboardToPDF } from '../../utils/pdfExport';
import './Dashboard.css';

const { Content } = Layout;
const { Text, Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// ─── Status Tag Colors ─────────────────────────────────────────────────────────
const statusColors = {
    TECHNICAL: 'orange',
    CULTURAL:  'green',
    FINAL:     'blue',
    INTRO:     'default',
};

// ─── KPI Card Component ────────────────────────────────────────────────────────
const KPICard = ({ label, value, allTime, icon, isStatic }) => {
    return (
        <Card className={`gh-kpi-card ${isStatic ? 'gh-kpi-card-static' : ''}`} bodyStyle={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div className="gh-kpi-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {icon}
                <span>{label}</span>
            </div>
            <div className="gh-kpi-value-row">
                <span className="gh-kpi-value">{value.toLocaleString()}</span>
            </div>
            {allTime !== undefined && (
                <div style={{ marginTop: 'auto', paddingTop: 6, fontSize: 12, color: '#607d8b', fontWeight: 500 }}>
                    {allTime.toLocaleString()} total
                </div>
            )}
        </Card>
    );
};

// ─── Outbound per User (inline bars) ───────────────────────────────────────────
const OutboundBarsInline = ({ data }) => {
    const maxVal = Math.max(...data.map((d) => d.count));
    return (
        <div style={{ paddingTop: 8 }}>
            {data.map((d) => (
                <div className="gh-bar-user-row" key={d.user}>
                    <span className="gh-bar-user-name">{d.user}</span>
                    <div className="gh-bar-track">
                        <div
                            className="gh-bar-fill"
                            style={{
                                width: `${(d.count / maxVal) * 100}%`,
                                background: '#38b549',
                            }}
                        />
                    </div>
                    <span className="gh-bar-count">{d.count.toLocaleString()}</span>
                </div>
            ))}
        </div>
    );
};

// ─── Main Dashboard Component ──────────────────────────────────────────────────
const formatDateRange = (dates) => {
    if (!dates || !dates[0] || !dates[1]) return 'Selected Date Range';
    return `${dates[0].format('MMM D, YYYY')} - ${dates[1].format('MMM D, YYYY')}`;
};

const DashboardV2 = (props) => {
    const {
        loading,
        filters,
        kpis,
        interviews,
        scheduledPerMonth,
        newContacts,
        messagingVolume,
        outboundPerUser,
        setFilters,
        fetchDashboard,
    } = useDashboardStore();

    // Feature-RBAC: without `reporting` the analytics half of this page (KPIs,
    // charts, PDF export) is locked. The Interviews table/calendar stays — it's
    // an operational list, not analytics, and comes from the same fetch.
    const hasReporting = useFeature('reporting');

    useEffect(() => {
        if (props.selectedCompany) {
            fetchDashboard(props.selectedCompany);
        }
    }, [props.selectedCompany, filters.dateRange, filters.timezone, filters.groupBy, fetchDashboard]);

    // State for maxmized chart overlay
    const [maximizedChart, setMaximizedChart] = useState({
        visible: false,
        title: '',
        type: '', // 'line', 'column', 'groupedColumn', 'bar'
        config: null,
    });

    const openMaximized = (title, type, config) => {
        setMaximizedChart({
            visible: true,
            title,
            type,
            // Override height for maximized view
            config: { ...config, height: 600 }
        });
    };

    const closeMaximized = () => {
        setMaximizedChart(prev => ({ ...prev, visible: false }));
    };

    // State for the expandable Interviews modal (list <-> calendar)
    const [interviewsModalVisible, setInterviewsModalVisible] = useState(false);
    const [interviewsView, setInterviewsView] = useState('list'); // 'list' | 'calendar'

    // FullCalendar ref + toolbar state (custom antd-styled controls)
    const calendarRef = React.useRef(null);
    const [calendarView, setCalendarView] = useState('dayGridMonth');
    const [calendarTitle, setCalendarTitle] = useState('');

    const calendarNav = (action) => {
        const api = calendarRef.current && calendarRef.current.getApi();
        if (!api) return;
        if (action === 'prev') api.prev();
        else if (action === 'next') api.next();
        else if (action === 'today') api.today();
    };

    const changeCalendarView = (view) => {
        const api = calendarRef.current && calendarRef.current.getApi();
        if (api) api.changeView(view);
        setCalendarView(view);
    };

    // FullCalendar mis-measures its width when mounted inside a modal that is still
    // animating open; recompute once the modal transition settles.
    const handleInterviewsModalOpenChange = (open) => {
        if (open && interviewsView === 'calendar') {
            const api = calendarRef.current && calendarRef.current.getApi();
            if (api) setTimeout(() => api.updateSize(), 0);
        }
    };

    const goToContact = (contactId) => {
        if (!contactId) return;
        setInterviewsModalVisible(false);
        props.history.push(`/contacts/${contactId}`);
    };

    // FullCalendar events derived from the interviews list
    const interviewEvents = React.useMemo(() => (
        interviews
            .filter(i => i.start)
            .map(i => ({
                id: i.key,
                title: i.candidate,
                start: i.start,
                end: i.end,
                extendedProps: { contactId: i.contactId, user: i.user },
            }))
    ), [interviews]);

    // Open the calendar on the selected period rather than today
    const calendarInitialDate = (filters.dateRange && filters.dateRange[0])
        ? filters.dateRange[0].format('YYYY-MM-DD')
        : undefined;

    const [isExporting, setIsExporting] = useState(false);

    const handleExportPDF = async () => {
        setIsExporting(true);
        try {
            // Find company name from props or store if possible, 
            let companyName = 'GoHire';
            if (props.companyList && props.companyList.length > 0 && props.selectedCompany) {
                const found = props.companyList.find(c => String(c.id) === String(props.selectedCompany));
                if (found && found.brand_name) {
                    companyName = found.brand_name;
                } else if (found && found.name) {
                    companyName = found.name;
                }
            }

            await exportDashboardToPDF({
                elementId: 'dashboard-export-content',
                interviews: interviews,
                companyName: companyName,
                dateRangeStr: formatDateRange(filters.dateRange),
                timezone: filters.timezone,
            });
        } catch (error) {
            console.error("PDF Export failed", error);
        } finally {
            setIsExporting(false);
        }
    };

    const timeZones = React.useMemo(() => {
        return moment.tz.names().map(tz => {
            const offset = moment.tz(tz).format('Z');
            return {
                label: `(GMT${offset}) ${tz}`,
                value: tz
            };
        });
    }, []);

    // ── Interview Table Columns ──────────────────────────────────────────
    const interviewColumns = [
        {
            title: 'Contact',
            dataIndex: 'candidate',
            key: 'candidate',
            width: 140,
            render: (text) => <Text strong style={{ fontSize: 13 }}>{text}</Text>,
        },
        {
            title: 'Schedule',
            dataIndex: 'datetime',
            key: 'datetime',
            width: 160,
            render: (text) => <Text style={{ fontSize: 13 }}>{text}</Text>,
        },
        {
            title: 'User',
            dataIndex: 'user',
            key: 'user',
            width: 120,
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <UserAvatar name={text} userId={record.userId} size="small" />
                    <Text style={{ fontSize: 13 }}>{text}</Text>
                </div>
            ),
        },
    ];

    // ── Expanded Interview Table Columns (deep-link contact) ─────────────
    const interviewModalColumns = [
        {
            ...interviewColumns[0],
            render: (text, record) => (
                <Link to={`/contacts/${record.contactId}`} onClick={() => setInterviewsModalVisible(false)}>
                    <Text strong style={{ fontSize: 13, color: '#1890ff', cursor: 'pointer' }}>{text}</Text>
                </Link>
            ),
        },
        interviewColumns[1],
        interviewColumns[2],
    ];

    // ── Export Interview Table Columns (no avatars) ──────────────────────
    const exportInterviewColumns = [
        {
            title: 'Contact',
            dataIndex: 'candidate',
            key: 'candidate',
            width: 140,
            render: (text) => <Text strong style={{ fontSize: 13 }}>{text}</Text>,
        },
        {
            title: 'Schedule',
            dataIndex: 'datetime',
            key: 'datetime',
            width: 160,
            render: (text) => <Text style={{ fontSize: 13 }}>{text}</Text>,
        },
        {
            title: 'User',
            dataIndex: 'user',
            key: 'user',
            width: 120,
            render: (text) => <Text style={{ fontSize: 13 }}>{text}</Text>,
        },
    ];

    // ── Chart Configs (G2 5.x / @ant-design/charts v2 API) ────────────────

    // Scheduled Interviews per Month (Line)
    const scheduledLineConfig = {
        data: scheduledPerMonth,
        xField: 'month',
        yField: 'volume',
        shapeField: 'smooth',
        style: {
            stroke: '#38b54988',
            lineWidth: 1.5,
        },
        point: {
            shapeField: 'circle',
            sizeField: 4,
            style: { fill: '#38b549', stroke: '#fff', lineWidth: 2 },
        },
        scale: {
            y: { nice: true, tickCount: 5 },
        },
        axis: {
            x: { labelFontSize: 11, labelFill: '#666' },
            y: {
                labelFontSize: 11,
                labelFill: '#666',
                labelFormatter: (v) => Number.isInteger(v) ? String(v) : '',
                grid: true,
                gridStroke: '#f0f0f0',
                gridLineWidth: 1,
            },
        },
        tooltip: (d) => ({
            name: 'Interviews',
            value: d.volume,
        }),
        height: 220,
        autoFit: true,
    };

    // New Contacts / Month (Column)
    const contactsColumnConfig = {
        data: newContacts,
        xField: 'month',
        yField: 'count',
        style: {
            fill: '#5b9bd5',
            maxWidth: 30,
        },
        scale: {
            y: { nice: true, tickCount: 5 },
        },
        axis: {
            x: { labelFontSize: 11, labelFill: '#666' },
            y: {
                labelFontSize: 11,
                labelFill: '#666',
                labelFormatter: (v) => Number.isInteger(v) ? String(v) : '',
                grid: true,
                gridStroke: '#f0f0f0',
            },
        },
        height: 180,
        autoFit: true,
    };

    // Messaging Volume (Grouped Column)
    const messagingColumnConfig = {
        data: messagingVolume,
        xField: 'month',
        yField: 'count',
        colorField: 'type',
        group: true,
        style: {
            maxWidth: 20,
        },
        scale: {
            y: { nice: true, tickCount: 5 },
            color: {
                range: ['#1890ff', '#38b549'],
                domain: ['In', 'Out'],
            },
        },
        axis: {
            x: { labelFontSize: 11, labelFill: '#666' },
            y: {
                labelFontSize: 11,
                labelFill: '#666',
                labelFormatter: (v) => Number.isInteger(v) ? String(v) : '',
                grid: true,
                gridStroke: '#f0f0f0',
            },
        },
        legend: false,
        height: 180,
        autoFit: true,
    };

    // Outbound Msg / User (Donut)
    const outboundBarConfig = {
        data: outboundPerUser,
        angleField: 'count',
        colorField: 'user',
        innerRadius: 0.6,
        label: {
            text: 'count',
            style: {
                fontWeight: 'bold',
            },
        },
        legend: {
            color: {
                title: false,
                position: 'bottom',
                rowPadding: 5,
            },
        },
        tooltip: (d) => ({
            name: d.user,
            value: d.count,
        }),
        state: {
            active: { stroke: '#fff', lineWidth: 1 },
            inactive: { opacity: 0.5 },
        },
        height: 180,
        autoFit: true,
    };

    return (
        <Layout className="gh-dashboard" id="dashboard-export-content">
            {/* Hidden Table for PDF Engine to snapshot */}
            <div style={{ position: 'absolute', top: '-9999px', left: '-9999px', pointerEvents: 'none' }}>
                <div id="gh-dash-export-table-container" style={{ width: 1000, background: '#fff', padding: '0 24px 24px' }}>
                    <Title level={4} style={{ marginBottom: 16 }}>All Scheduled Interviews</Title>
                    <Table
                        columns={exportInterviewColumns}
                        dataSource={interviews}
                        pagination={false}
                        size="small"
                        rowKey="key"
                    />
                </div>
            </div>
            {isExporting && (
                <div id="gh-dash-loading-overlay" style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    zIndex: 9999,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <Spin size="large" />
                    <Title level={4} style={{ marginTop: 24 }}>Generating PDF Report...</Title>
                    <Text type="secondary">This may take a few seconds.</Text>
                </div>
            )}
            <Content>
                {/* ── Filter Bar ────────────────────────────────────────── */}
                <div className="gh-dash-filter-bar" id="gh-dash-filter-bar">
                    <Title level={4} style={{ margin: 0 }}>Dashboard <PageTourButton steps={getDashboardSteps()} /></Title>
                    <div style={{ flex: 1 }} />
                    <Space size="middle">
                        <RangePicker
                            size="middle"
                            value={filters.dateRange}
                            onChange={(dates) => setFilters({ dateRange: dates })}
                            ranges={{
                                'Last 30 Days': [dayjs().subtract(30, 'days'), dayjs()],
                                'Last 60 Days': [dayjs().subtract(60, 'days'), dayjs()],
                                'Last 90 Days': [dayjs().subtract(90, 'days'), dayjs()],
                                'This Month': [dayjs().startOf('month'), dayjs().endOf('month')],
                                'This Year': [dayjs().startOf('year'), dayjs().endOf('year')],
                                'Last Year': [dayjs().subtract(1, 'year').startOf('year'), dayjs().subtract(1, 'year').endOf('year')],
                            }}
                            suffixIcon={<CalendarOutlined />}
                            style={{ minWidth: 240 }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Select
                                showSearch
                                value={filters.timezone}
                                onChange={(val) => setFilters({ timezone: val })}
                                style={{ minWidth: 260 }}
                                placeholder="Select timezone"
                                optionFilterProp="children"
                            >
                                {timeZones.map((tz, idx) => (
                                    <Option key={idx} value={tz.value}>{tz.label}</Option>
                                ))}
                            </Select>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Text style={{ color: '#546e7a', fontSize: 13 }}>Group by:</Text>
                            <Segmented
                                className="gh-dash-groupby"
                                size="small"
                                value={filters.groupBy}
                                onChange={(val) => setFilters({ groupBy: val })}
                                options={[
                                    { label: 'Auto', value: 'auto' },
                                    { label: 'Day', value: 'day' },
                                    { label: 'Week', value: 'week' },
                                    { label: 'Month', value: 'month' },
                                ]}
                            />
                        </div>
                    </Space>
                </div>

                <Spin spinning={loading} size="large">
                    {/* ── KPI Cards ─────────────────────────────────────── */}
                    <div className="gh-dash-kpi-row">
                        {!hasReporting ? (
                            <FeatureLockCard featureKey="reporting" height={140} />
                        ) : (
                        <Row gutter={[16, 16]} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch' }}>
                            <Col xs={12} sm={8} lg={4} flex="1">
                                <KPICard label="Inbound MSG" value={kpis.inboundMsg.value} allTime={kpis.inboundMsg.allTime} icon={<MessageOutlined />} />
                            </Col>
                            <Col xs={12} sm={8} lg={4} flex="1">
                                <KPICard label="Outbound MSG" value={kpis.outboundMsg.value} allTime={kpis.outboundMsg.allTime} icon={<SendOutlined />} />
                            </Col>
                            <Col xs={12} sm={8} lg={4} flex="1">
                                <KPICard label="Scheduled" value={kpis.scheduled.value} allTime={kpis.scheduled.allTime} icon={<CalendarOutlined />} />
                            </Col>
                            <Col xs={12} sm={8} lg={4} flex="1">
                                <KPICard label="Contacts" value={kpis.contacts.value} allTime={kpis.contacts.allTime} icon={<ContactsOutlined />} />
                            </Col>
                            <Col xs={12} sm={8} lg={4} flex="1">
                                <KPICard label="Users Count" value={kpis.usersCount.value} icon={<TeamOutlined />} isStatic={true} />
                            </Col>
                            <Col xs={12} sm={8} lg={4} flex="1" id="gh-dash-export-card">
                                <Card className="gh-kpi-card gh-kpi-card-static" bodyStyle={{ padding: '16px 20px', height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <div className="gh-kpi-label" style={{ marginBottom: 12 }}>Export Data</div>
                                    <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
                                        <Button size="small" icon={<FilePdfOutlined />} loading={isExporting} onClick={handleExportPDF} style={{ flex: 1, fontSize: 11, padding: '0 4px' }}>PDF</Button>
                                    </div>
                                </Card>
                            </Col>
                        </Row>
                        )}
                    </div>

                    {/* ── Middle Row: Interviews Table + Scheduled Chart ── */}
                    <div className="gh-dash-grid">
                        <Row gutter={[16, 16]}>
                            <Col xs={24} lg={12} id="gh-dash-interviews-col">
                                <Card
                                    className="gh-dash-card"
                                    title="Interviews"
                                    extra={
                                        <Button
                                            type="text"
                                            icon={<ExpandAltOutlined />}
                                            onClick={() => setInterviewsModalVisible(true)}
                                            style={{ color: '#999' }}
                                        />
                                    }
                                >
                                    <Table
                                        className="gh-dash-interview-table"
                                        columns={interviewColumns}
                                        dataSource={interviews}
                                        pagination={false}
                                        size="small"
                                        rowKey="key"
                                        scroll={{ y: 260 }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} lg={12} id="gh-dash-scheduled-col">
                                {!hasReporting ? (
                                    <FeatureLockCard featureKey="reporting" height={340} />
                                ) : (
                                <Card
                                    className="gh-dash-card"
                                    title={`Scheduled Interviews`}
                                    extra={
                                        <Space size="middle" align="center">
                                            <div className="gh-chart-legend" style={{ margin: 0 }}>
                                                <div className="gh-chart-legend-item">
                                                    <span className="gh-chart-legend-dot" style={{ background: '#38b549' }} />
                                                    Volume
                                                </div>
                                            </div>
                                            <Button 
                                                type="text" 
                                                icon={<ExpandAltOutlined />} 
                                                onClick={() => openMaximized('Scheduled Interviews', 'line', scheduledLineConfig)} 
                                                style={{ color: '#999' }}
                                            />
                                        </Space>
                                    }
                                >
                                    <Line {...scheduledLineConfig} />
                                </Card>
                                )}
                            </Col>
                        </Row>
                    </div>

                    {/* ── Bottom Row: 3 Charts ─────────────────────────── */}
                    {hasReporting && (
                    <div className="gh-dash-grid" style={{ paddingTop: 0 }}>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={8}>
                                <Card 
                                    className="gh-dash-card" 
                                    title={`New Contacts`}
                                    extra={
                                        <Button 
                                            type="text" 
                                            icon={<ExpandAltOutlined />} 
                                            onClick={() => openMaximized('New Contacts', 'column', contactsColumnConfig)} 
                                            style={{ color: '#999' }}
                                        />
                                    }
                                >
                                    <Column {...contactsColumnConfig} />
                                </Card>
                            </Col>
                            <Col xs={24} md={8}>
                                <Card
                                    className="gh-dash-card"
                                    title="Messaging Volume"
                                    extra={
                                        <Space size="middle" align="center">
                                            <div className="gh-chart-legend" style={{ margin: 0 }}>
                                                <div className="gh-chart-legend-item">
                                                    <span className="gh-chart-legend-dot" style={{ background: '#1890ff' }} />
                                                    In
                                                </div>
                                                <div className="gh-chart-legend-item">
                                                    <span className="gh-chart-legend-dot" style={{ background: '#38b549' }} />
                                                    Out
                                                </div>
                                            </div>
                                            <Button 
                                                type="text" 
                                                icon={<ExpandAltOutlined />} 
                                                onClick={() => openMaximized('Messaging Volume', 'groupedColumn', messagingColumnConfig)} 
                                                style={{ color: '#999' }}
                                            />
                                        </Space>
                                    }
                                >
                                    <Column {...messagingColumnConfig} />
                                </Card>
                            </Col>
                            <Col xs={24} md={8}>
                                <Card 
                                    className="gh-dash-card" 
                                    title="Outbound Msg / User"
                                    extra={
                                        <Button 
                                            type="text" 
                                            icon={<ExpandAltOutlined />} 
                                    onClick={() => openMaximized('Outbound Msg / User', 'pie', outboundBarConfig)}
                                            style={{ color: '#999' }}
                                        />
                                    }
                                >
                                    <Pie {...outboundBarConfig} />
                                </Card>
                            </Col>
                        </Row>
                    </div>
                    )}
                </Spin>

                {/* ── Maximized Chart Modal ───────────────────────────────── */}
                <Modal
                    title={<Title level={4} style={{ margin: 0 }}>{maximizedChart.title}</Title>}
                    visible={maximizedChart.visible}
                    onCancel={closeMaximized}
                    footer={null}
                    width={1000}
                    centered
                    destroyOnClose
                    bodyStyle={{ padding: '24px 24px 8px' }}
                >
                    {maximizedChart.visible && maximizedChart.config && (
                        <div>
                            {maximizedChart.type === 'line' && <Line {...maximizedChart.config} />}
                            {maximizedChart.type === 'column' && <Column {...maximizedChart.config} />}
                            {maximizedChart.type === 'groupedColumn' && <Column {...maximizedChart.config} />}
                            {maximizedChart.type === 'pie' && <Pie {...maximizedChart.config} />}
                        </div>
                    )}
                </Modal>

                {/* ── Expanded Interviews Modal (List / Calendar) ─────────── */}
                <Modal
                    title={
                        <Space size="large" align="center">
                            <Title level={4} style={{ margin: 0 }}>Interviews</Title>
                            <Segmented
                                options={[
                                    { label: 'List', value: 'list' },
                                    { label: 'Calendar', value: 'calendar' },
                                ]}
                                value={interviewsView}
                                onChange={setInterviewsView}
                            />
                        </Space>
                    }
                    visible={interviewsModalVisible}
                    onCancel={() => setInterviewsModalVisible(false)}
                    afterOpenChange={handleInterviewsModalOpenChange}
                    footer={null}
                    width={1000}
                    centered
                    destroyOnClose
                    bodyStyle={{ padding: '24px 24px 8px' }}
                >
                    {interviewsView === 'list' && (
                        <Table
                            className="gh-dash-interview-table"
                            columns={interviewModalColumns}
                            dataSource={interviews}
                            pagination={false}
                            size="small"
                            rowKey="key"
                            scroll={{ y: 500 }}
                        />
                    )}
                    {interviewsView === 'calendar' && (
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 12 }}>
                                <Space>
                                    <Button icon={<LeftOutlined />} onClick={() => calendarNav('prev')} />
                                    <Button icon={<RightOutlined />} onClick={() => calendarNav('next')} />
                                    <Button onClick={() => calendarNav('today')}>Today</Button>
                                </Space>
                                <Title level={5} style={{ margin: 0, textAlign: 'center', flex: 1 }}>{calendarTitle}</Title>
                                <Segmented
                                    value={calendarView}
                                    onChange={changeCalendarView}
                                    options={[
                                        { label: 'Month', value: 'dayGridMonth' },
                                        { label: 'Week', value: 'timeGridWeek' },
                                        { label: 'Day', value: 'timeGridDay' },
                                    ]}
                                />
                            </div>
                            <FullCalendar
                                ref={calendarRef}
                                plugins={[dayGridPlugin, timeGridPlugin]}
                                initialView="dayGridMonth"
                                initialDate={calendarInitialDate}
                                headerToolbar={false}
                                events={interviewEvents}
                                eventClick={(arg) => goToContact(arg.event.extendedProps.contactId)}
                                datesSet={(arg) => {
                                    setCalendarTitle(arg.view.title);
                                    setCalendarView(arg.view.type);
                                }}
                                height={560}
                                eventDisplay="block"
                            />
                        </div>
                    )}
                </Modal>
            </Content>
        </Layout>
    );
};

const mapStateToProps = (state) => {
    const { company: { selectedCompany, companyList } } = state;
    const userRole = session.get('userData')?.role || '';
    const isAdmin = userRole === config.userRoles.ghAdmin;

    let companyId = selectedCompany;
    if (!isAdmin) {
        companyId = session.get('userData')?.companyId;
    }

    return {
        selectedCompany: companyId,
        companyList,
        isAdmin,
    };
};

export default withRouter(connect(mapStateToProps)(DashboardV2));
